import { useEffect, useMemo, useRef, useState } from 'react';
import { MessageSquare, Send, X } from 'lucide-react';
import { getApiBaseUrl } from '../../lib/apiBase';

function buildConversationId() {
  return `conv_${Math.random().toString(16).slice(2)}_${Date.now()}`;
}

const API_BASE_URL = getApiBaseUrl();

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [userInfo, setUserInfo] = useState({ name: '', email: '' });
  const [showUserInfoForm, setShowUserInfoForm] = useState(false);
  const [pendingUserMessage, setPendingUserMessage] = useState('');
  const [conversationId, setConversationId] = useState('');
  const [quickReplies, setQuickReplies] = useState([]);
  const [openingLinks, setOpeningLinks] = useState([]);
  const [showCareersLink, setShowCareersLink] = useState(false);

  const listRef = useRef(null);

  const initialGreeting = useMemo(
    () => [
      {
        id: `a_${Date.now()}`,
        role: 'assistant',
        content:
          'Hi, I’m the Libsys support assistant. How can I help today—services or support?',
        createdAt: new Date().toISOString(),
      },
    ],
    []
  );

  useEffect(() => {
    // Reset chat each time this component mounts.
    // (Prevents old conversations from coming back by default.)
    setConversationId(buildConversationId());
    setMessages([]);
    setUserInfo({ name: '', email: '' });
    setQuickReplies([]);
    setOpeningLinks([]);
    setShowCareersLink(false);
  }, []);

  useEffect(() => {
    // Let ScrollToTop hide itself while chat is open.
    window.dispatchEvent(new CustomEvent('libsys-chat-open', { detail: { open: isOpen } }));
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    // When opening, greet if empty.
    if (messages.length === 0) setMessages(initialGreeting);
  }, [isOpen, messages.length, initialGreeting]);

  useEffect(() => {
    if (!isOpen) return;
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [isOpen, messages, loading]);

  async function requestWithTimeout(url, options, timeoutMs = 12000) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    try {
      return await fetch(url, { ...options, signal: controller.signal });
    } finally {
      clearTimeout(timeoutId);
    }
  }

  async function callChatApi({ text, skipUserMessage }) {
    const page = window.location?.pathname || '';
    const payload = {
      message: text,
      name: userInfo.name,
      email: userInfo.email,
      conversationId,
      page,
    };

    let resp;
    try {
      resp = await requestWithTimeout(
        `${API_BASE_URL}/api/chat`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        },
        12000
      );
    } catch (firstErr) {
      // One quick retry for transient backend/network hiccups.
      await new Promise((resolve) => setTimeout(resolve, 350));
      try {
        resp = await requestWithTimeout(
          `${API_BASE_URL}/api/chat`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          },
          12000
        );
      } catch (retryErr) {
        const isTimeout = retryErr?.name === 'AbortError';
        throw new Error(
          isTimeout
            ? 'Chat is taking too long. Please try again.'
            : 'Unable to reach chat server. Please try again.'
        );
      }
    }

    const data = await resp.json().catch(() => ({}));
    if (!resp.ok) {
      throw new Error(data?.error || 'Chat request failed');
    }
    return data;
  }

  async function handleSend(text) {
    const trimmed = String(text || '').trim();
    if (!trimmed || loading) return;

    let needsAny = false;
    const userMsg = {
      id: `u_${Date.now()}`,
      role: 'user',
      content: trimmed,
      createdAt: new Date().toISOString(),
    };

    setQuickReplies([]);
    setOpeningLinks([]);
    setShowCareersLink(false);
    setLoading(true);

    try {
      setMessages((prev) => [...prev, userMsg]);

      const data = await callChatApi({ text: trimmed, skipUserMessage: showUserInfoForm });

      // Ensure we keep the same conversation id.
      if (data.conversationId && data.conversationId !== conversationId) {
        setConversationId(data.conversationId);
      }

      // Add assistant reply.
      const assistantMsg = {
        id: `a_${Date.now()}`,
        role: 'assistant',
        content: data.reply || 'Please contact support',
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, assistantMsg]);

      const needs = data.needsUserInfo || { name: false, email: false };
      needsAny = Boolean(needs?.name || needs?.email);
      if (needsAny) {
        setPendingUserMessage(trimmed);
        setShowUserInfoForm(true);
        // Keep assistant reply that asked for info.
        return;
      }

      setQuickReplies([...(data.followUpQuestions || []), ...(data.suggestions || [])].slice(0, 6));
      setOpeningLinks(Array.isArray(data.openingLinks) ? data.openingLinks : []);
      setShowCareersLink(Boolean(data.showCareersLink));
      setInput('');
    } catch (e) {
      const assistantMsg = {
        id: `a_${Date.now()}`,
        role: 'assistant',
        content: e?.message || 'Please contact support',
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, assistantMsg]);
    } finally {
      setLoading(false);
      if (!needsAny) {
        setShowUserInfoForm(false);
        setPendingUserMessage('');
      }
    }
  }

  async function submitUserInfo() {
    const name = String(userInfo.name || '').trim();
    const email = String(userInfo.email || '').trim();
    if (!name || !email) return;
    setLoading(true);
    setShowUserInfoForm(false);

    try {
      const data = await callChatApi({ text: pendingUserMessage, skipUserMessage: true });
      const assistantMsg = {
        id: `a_${Date.now()}`,
        role: 'assistant',
        content: data.reply || 'Please contact support',
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, assistantMsg]);
      setQuickReplies([...(data.followUpQuestions || []), ...(data.suggestions || [])].slice(0, 6));
      setOpeningLinks(Array.isArray(data.openingLinks) ? data.openingLinks : []);
      setShowCareersLink(Boolean(data.showCareersLink));
    } catch (e) {
      const assistantMsg = {
        id: `a_${Date.now()}`,
        role: 'assistant',
        content: e?.message || 'Please contact support',
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, assistantMsg]);
    } finally {
      setLoading(false);
      setPendingUserMessage('');
    }
  }

  return (
    <>
      {!isOpen ? (
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          aria-label="Open chat"
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-primary text-white shadow-xl hover:bg-secondary transition-all flex items-center justify-center"
        >
          <MessageSquare className="w-6 h-6" />
        </button>
      ) : (
        <div className="fixed bottom-6 right-6 z-50 w-[92vw] max-w-[380px] h-[600px] max-h-[85vh] flex flex-col bg-white border border-slate-200 rounded-2xl shadow-[0_18px_60px_-35px_rgba(15,23,42,0.35)] overflow-hidden">
          <div className="bg-primary text-white px-4 py-3 flex items-center justify-between gap-3 flex-shrink-0">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              <div className="leading-tight">
                <p className="font-black text-sm">Libsys Assistant</p>
              </div>
            </div>
            <button type="button" aria-label="Close chat" onClick={() => setIsOpen(false)} className="p-2 rounded-xl hover:bg-primary/90 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto p-3 bg-slate-50">
            {messages.map((m) => (
              <div key={m.id} className={`mb-3 flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                  className={`max-w-[85%] rounded-2xl px-3 py-2 text-[13px] leading-[1.35] border ${
                    m.role === 'user'
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-slate-800 border-slate-200'
                  } whitespace-pre-line`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="mb-3 flex justify-start">
                <div className="max-w-[85%] rounded-2xl bg-white border border-slate-200 px-3 py-2 text-sm text-slate-600">
                  Typing...
                </div>
              </div>
            )}
          </div>

          {quickReplies.length > 0 && (
            <div className="px-3 pb-2 bg-white flex-shrink-0">
              <div className="flex flex-wrap gap-2 justify-start">
                {quickReplies.map((q, idx) => (
                  <button
                    key={`${q}-${idx}`}
                    type="button"
                    onClick={() => handleSend(q)}
                    className="px-3 py-1.5 rounded-xl text-xs font-black bg-slate-100 hover:bg-slate-200 transition-colors text-slate-800"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {(openingLinks.length > 0 || showCareersLink) && (
            <div className="px-3 pb-2 bg-white flex-shrink-0">
              <p className="text-[11px] font-black uppercase tracking-[0.12em] text-slate-500 mb-2">Current Openings</p>
              <div className="flex flex-col gap-1.5 max-h-28 overflow-y-auto">
                {openingLinks.map((job, idx) => (
                  <a
                    key={`${job.href}-${idx}`}
                    href={job.href}
                    className="text-[12px] font-semibold text-primary hover:text-secondary underline underline-offset-2"
                  >
                    {job.label}
                  </a>
                ))}
                {showCareersLink && (
                  <a
                    href="/careers"
                    className="text-[12px] font-black text-primary hover:text-secondary underline underline-offset-2 mt-1"
                  >
                    View all openings
                  </a>
                )}
              </div>
            </div>
          )}

          {showUserInfoForm ? (
            <div className="p-3 bg-white border-t border-slate-200 flex-shrink-0">
              <p className="text-xs font-black text-slate-900 mb-2">To help you faster, please share your details.</p>
              <div className="grid gap-2">
                <input
                  value={userInfo.name}
                  onChange={(e) => setUserInfo((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Your name"
                  className="w-full p-3 rounded-xl border border-slate-200 outline-none focus:border-primary bg-white text-sm"
                />
                <input
                  value={userInfo.email}
                  onChange={(e) => setUserInfo((p) => ({ ...p, email: e.target.value }))}
                  placeholder="Email address"
                  className="w-full p-3 rounded-xl border border-slate-200 outline-none focus:border-primary bg-white text-sm"
                />
                <button
                  type="button"
                  onClick={submitUserInfo}
                  className="w-full px-4 py-3 rounded-xl bg-primary text-white font-black hover:bg-secondary transition-colors disabled:opacity-60"
                  disabled={loading}
                >
                  Continue
                </button>
              </div>
            </div>
          ) : (
            <div className="p-3 bg-white border-t border-slate-200 flex gap-2 items-center flex-shrink-0">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your message..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSend(input);
                }}
                className="flex-1 p-3 rounded-xl border border-slate-200 outline-none focus:border-primary bg-white text-sm"
              />
              <button
                type="button"
                onClick={() => handleSend(input)}
                className="w-12 h-12 rounded-xl bg-primary text-white font-black hover:bg-secondary transition-colors disabled:opacity-60 flex items-center justify-center"
                disabled={loading}
                aria-label="Send message"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}

